import { matchPath } from 'react-router-dom';

import { canonicalizeShellPathname, shellPaths } from '@/core/router/app-paths';
import { resolveTabForPath, TAB_MORE } from '@/core/router/app-tabs';
import { isShellObjectIdParam } from '@/core/router/shell-object-id-param';

export type ShellDetailRoute = {
  pattern: string;
  parent: string;
  /** When set, the named param must be a Mongo ObjectId (RR6 has no path regex). */
  objectIdParam?: string;
};

/**
 * Detail routes that need a parent list route seeded before cold navigation
 * (push notifications, deep links, toast taps).
 */
export const SHELL_DETAIL_ROUTES: readonly ShellDetailRoute[] = [
  {
    pattern: `${shellPaths.leads}/public/:workspaceUsername/:username`,
    parent: shellPaths.leads,
  },
  {
    pattern: `${shellPaths.leadsEvents}/:eventId`,
    parent: shellPaths.leadsEvents,
    objectIdParam: 'eventId',
  },
  {
    pattern: `${shellPaths.leads}/:id`,
    parent: shellPaths.leads,
    objectIdParam: 'id',
  },
  {
    pattern: `${shellPaths.moreProducts}/:productId`,
    parent: shellPaths.moreProducts,
    objectIdParam: 'productId',
  },
  /**
   * Static “detail-like” routes: cold deeplink / dialog entry must seed the
   * list parent so Ionic swipe-back works (DEV-469).
   */
  {
    pattern: shellPaths.moreProductsSetup,
    parent: shellPaths.moreProducts,
  },
  {
    pattern: shellPaths.moreWorkspaceBillingUpgrade,
    parent: shellPaths.moreWorkspaceBilling,
  },
  {
    pattern: shellPaths.moreWorkspaceLocationCreate,
    parent: shellPaths.moreWorkspaceLocations,
  },
  {
    pattern: shellPaths.profilesEdit,
    parent: shellPaths.profiles,
  },
  {
    pattern: shellPaths.profilesLinkCard,
    parent: shellPaths.profiles,
  },
  {
    pattern: `${shellPaths.moreWorkspaceLocations}/:id`,
    parent: shellPaths.moreWorkspaceLocations,
    objectIdParam: 'id',
  },
  {
    pattern: `${shellPaths.morePeople}/teams/:id`,
    parent: `${shellPaths.morePeople}?tab=teams`,
  },
  {
    pattern: `${shellPaths.moreProfileTemplates}/:templateId`,
    parent: shellPaths.moreProfileTemplates,
  },
  {
    pattern: `${shellPaths.feedbackInvites}/:inviteId`,
    parent: `${shellPaths.feedbackActivity}?tab=sent`,
    objectIdParam: 'inviteId',
  },
  {
    pattern: `${shellPaths.feedbackForms}/:templateId`,
    parent: shellPaths.feedbackLibrary,
  },
  {
    pattern: `${shellPaths.feedbackMessages}/:templateId`,
    parent: `${shellPaths.feedbackLibrary}?tab=messages`,
  },
];
/** Parent list route for a shell detail pathname, if registered. */
export function getShellDetailParent(pathname: string): string | undefined {
  for (const route of SHELL_DETAIL_ROUTES) {
    const match = matchPath({ path: route.pattern, end: true }, pathname);
    if (!match) continue;
    if (route.objectIdParam) {
      const value = match.params[route.objectIdParam];
      if (!isShellObjectIdParam(value)) continue;
    }
    return route.parent;
  }
  return undefined;
}

export function isShellDetailPath(pathname: string): boolean {
  return getShellDetailParent(pathname) !== undefined;
}

const TAB_STACK_ROOTS = new Set<string>([
  shellPaths.home,
  shellPaths.profiles,
  shellPaths.leads,
  shellPaths.leadsEvents,
]);

function isMoreStackRoot(path: string, isNative: boolean): boolean {
  return isNative
    ? path === shellPaths.moreHub || path === shellPaths.more
    : path === shellPaths.more;
}

/** Tab / More hub roots — PageHeader must never show a back control here. */
export function isShellNavigationRoot(
  pathname: string,
  isNative: boolean,
): boolean {
  const path = canonicalizeShellPathname(pathname);
  return TAB_STACK_ROOTS.has(path) || isMoreStackRoot(path, isNative);
}

/**
 * Cap tab stack roots + peer hubs (e.g. Events / More hub) — no hierarchy back.
 * Alias of native `isShellNavigationRoot`.
 */
export function isShellTabRoot(pathname: string): boolean {
  return isShellNavigationRoot(pathname, true);
}

function isMoreStackSubPage(path: string, isNative: boolean): boolean {
  return (
    resolveTabForPath(path) === TAB_MORE && !isMoreStackRoot(path, isNative)
  );
}

/**
 * Declarative back target for in-shell pages (device-aware).
 * Returns `undefined` on tab stack roots where back must not appear.
 */
export function getShellBackTarget(
  pathname: string,
  isNative: boolean,
): string | undefined {
  const path = canonicalizeShellPathname(pathname);

  const detailParent = getShellDetailParent(path);
  if (detailParent) {
    return detailParent;
  }

  if (path === shellPaths.moreProductsSetup) {
    return shellPaths.moreProducts;
  }

  if (path === shellPaths.moreWorkspaceBillingUpgrade) {
    return shellPaths.moreWorkspaceBilling;
  }

  if (path === shellPaths.moreWorkspaceLocationCreate) {
    return shellPaths.moreWorkspaceLocations;
  }

  if (path === shellPaths.moreWorkspaceCreate) {
    return isNative ? shellPaths.moreHub : shellPaths.home;
  }

  if (TAB_STACK_ROOTS.has(path) || isMoreStackRoot(path, isNative)) {
    return undefined;
  }

  if (isNative && isMoreStackSubPage(path, isNative)) {
    return shellPaths.moreHub;
  }

  if (!isNative && path.startsWith(`${shellPaths.more}/`)) {
    return shellPaths.more;
  }

  return undefined;
}

/** Whether the current pathname is a page that should render a hierarchy back button. */
export function shouldShowShellBackButton(
  pathname: string,
  isNative: boolean,
): boolean {
  return getShellBackTarget(pathname, isNative) !== undefined;
}
